import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReligionType } from '@/hooks/useReligionSettings';
import ReligionConfirmSheet from './ReligionConfirmSheet';

interface ReligionSelectionScreenProps {
  onBack: () => void;
  onSelect: (religion: ReligionType) => void;
  selectedReligion?: ReligionType;
  isLoading?: boolean;
}

const religions: { type: ReligionType; label: string; icon: string; color: string }[] = [
  { type: 'jewish', label: 'Judaism', icon: '✡️', color: 'from-blue-500 to-blue-600' },
  { type: 'christian', label: 'Christian', icon: '✝️', color: 'from-orange-500 to-red-500' },
  { type: 'muslim', label: 'Muslim', icon: '☪️', color: 'from-green-500 to-emerald-600' },
  { type: 'hindu', label: 'Hindu', icon: '🕉️', color: 'from-orange-400 to-amber-500' },
];

const ReligionSelectionScreen: React.FC<ReligionSelectionScreenProps> = ({
  onBack,
  onSelect,
  selectedReligion,
  isLoading = false
}) => {
  const [tappedReligion, setTappedReligion] = useState<ReligionType | null>(null);
  const [showConfirmSheet, setShowConfirmSheet] = useState(false);

  const handleTap = (religion: ReligionType) => {
    setTappedReligion(religion);
    setShowConfirmSheet(true);
  };

  const handleConfirm = () => {
    if (tappedReligion) {
      onSelect(tappedReligion);
      setShowConfirmSheet(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmSheet(false);
    setTappedReligion(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-bold text-foreground">Religion Tab</h1>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Decorative diamonds */}
      <div className="flex justify-center gap-2 mb-6">
        <div className="w-2 h-2 bg-amber-400 rotate-45" />
        <div className="w-2 h-2 bg-orange-400 rotate-45" />
        <div className="w-2 h-2 bg-amber-400 rotate-45" />
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-center text-foreground mb-8">
        Choose Your Faith
      </h2>

      {/* Religion Cards Grid */}
      <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
        {religions.map((religion) => {
          const isSelected = selectedReligion === religion.type;
          const isTapped = tappedReligion === religion.type;
          const isOtherTapped = tappedReligion && tappedReligion !== religion.type;
          
          return (
            <button
              key={religion.type}
              onClick={() => handleTap(religion.type)}
              disabled={isLoading}
              className={`relative p-6 rounded-2xl bg-gradient-to-br ${religion.color} text-white shadow-lg transition-all duration-300 ease-out
                ${isTapped ? 'scale-110 shadow-2xl ring-4 ring-white/50' : 'hover:scale-105 active:scale-95'}
                ${isOtherTapped ? 'opacity-50 scale-95' : ''}
                ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
              `}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              )}
              <span className="text-4xl mb-2 block">{religion.icon}</span>
              <span className="font-semibold text-sm">{religion.label}</span>
            </button>
          );
        })}
      </div>

      {/* Other option */}
      <div className="mt-6 max-w-sm mx-auto">
        <button
          onClick={() => handleTap('other')}
          disabled={isLoading}
          className={`w-full p-4 rounded-2xl border-2 border-dashed transition-all duration-300
            ${tappedReligion === 'other' 
              ? 'border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-900/20 scale-105' 
              : 'border-muted-foreground/30 text-muted-foreground hover:border-amber-400 hover:text-amber-600'
            }
            ${tappedReligion && tappedReligion !== 'other' ? 'opacity-50 scale-95' : ''}
            ${selectedReligion === 'other' ? 'border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-900/20' : ''}
            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
          `}
        >
          <span className="text-xl mb-1 block">➕</span>
          <span className="font-medium text-sm">Other</span>
        </button>
      </div>

      {/* Decorative diamonds */}
      <div className="flex justify-center gap-2 mt-8">
        <div className="w-2 h-2 bg-amber-400 rotate-45" />
        <div className="w-2 h-2 bg-amber-400 rotate-45" />
        <div className="w-2 h-2 bg-amber-400 rotate-45" />
      </div>

      {/* Info text */}
      <p className="text-xs text-muted-foreground text-center mt-6 max-w-xs mx-auto">
        Your selection is private and can be changed anytime.
      </p>

      {/* Confirmation Sheet */}
      {tappedReligion && (
        <ReligionConfirmSheet
          isOpen={showConfirmSheet}
          religion={tappedReligion}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default ReligionSelectionScreen;
